import React, { useState, useEffect } from 'react';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { showErrorWithApiError, showSuccess } from '@/shared/utils/toast-utils';
import { ERROR, SUCCESS } from '@/config/toast-message-config';
import { CreateServiceAccountData } from '@/shared/types/serviceAccountTypes';
import { useCreateAccountServiceAccount } from '@/domains/service-accounts/services/account-service-accounts';

interface CreateServiceAccountDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const CreateServiceAccountDialog: React.FC<CreateServiceAccountDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const [formData, setFormData] = useState<CreateServiceAccountData>({
    name: '',
    clientSecret: '',
  });
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<keyof CreateServiceAccountData, string>>>({});

  const createServiceAccount = useCreateAccountServiceAccount();

  useEffect(() => {
    if (isOpen) {
      setFormData({ name: '', clientSecret: '' });
      setErrors({});
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof CreateServiceAccountData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData.clientSecret.trim()) {
      newErrors.clientSecret = 'Client secret is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      await createServiceAccount.mutateAsync({
        serviceAccountData: formData,
      });
      showSuccess(SUCCESS.SERVICE_ACCOUNT.CREATED);
      onClose();
    } catch (error) {
      showErrorWithApiError(error, ERROR.SERVICE_ACCOUNT.CREATE);
    }
  };

  const handleInputChange = (field: keyof CreateServiceAccountData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleClose = () => {
    setFormData({ name: '', clientSecret: '' });
    setErrors({});
    onClose();
  };

  const canSubmit =
    !createServiceAccount.isPending &&
    formData.name.trim().length >= 2 &&
    !!formData.clientSecret.trim();

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Service Account</DialogTitle>
          <DialogDescription>
            Enter the service account details. Name and Client Secret are required.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="service-account-name">Name *</Label>
            <Input
              id="service-account-name"
              type="text"
              placeholder="e.g. API Integration"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="service-account-client-secret">Client Secret *</Label>
            <div className="relative">
              <Input
                id="service-account-client-secret"
                type={showClientSecret ? 'text' : 'password'}
                placeholder="Enter client secret"
                value={formData.clientSecret}
                onChange={(e) => handleInputChange('clientSecret', e.target.value)}
                className={errors.clientSecret ? 'border-red-500' : ''}
                autoComplete="new-password"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowClientSecret(!showClientSecret)}
              >
                {showClientSecret ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </Button>
            </div>
            {errors.clientSecret && <p className="text-sm text-red-500">{errors.clientSecret}</p>}
          </div>

          <DialogFooter>
            <Button type="button"
              variant="outline"
              onClick={handleClose}
              disabled={createServiceAccount.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {createServiceAccount.isPending ? 'Creating...' : 'Add Service Account'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateServiceAccountDialog;
